/**
 * LaneReadView — read-only, lane-filtered pass through the script.
 *
 * Select timeline lanes → read just those lanes' scenes, in script order or in
 * story (chronological) order. Filtered-out scenes collapse into stubs: plain
 * "hidden" dividers in script order, "meanwhile:" markers in story order (the
 * timeline's columns are global time, so hidden scenes still have a real
 * position relative to what you're reading). Tap a stub to peek, tap a scene
 * to jump back into the full editor at that spot.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useIsMobile } from "@/lib/useMediaQuery";
import { BookOpen, X, Eye, EyeOff } from "lucide-react";

export function LaneReadView({ scriptId, elements, initialLaneIds, onClose, onJumpToScene }) {
  const isMobile = useIsMobile();
  const [tl, setTl] = useState(null);
  const [selected, setSelected] = useState(() => new Set(initialLaneIds || []));
  const [order, setOrder] = useState("script");
  const [peeks, setPeeks] = useState(() => new Set());

  useEffect(() => {
    api.get(`/scripts/${scriptId}/timeline`)
      .then(({ data }) => setTl(data))
      .catch(() => setTl({ lanes: [], scenes: [] }));
  }, [scriptId]);

  const lanes = useMemo(() => tl?.lanes || [], [tl]);
  const laneById = useMemo(() => Object.fromEntries(lanes.map((l) => [l.id, l])), [lanes]);
  const metaById = useMemo(() => {
    const m = {};
    for (const s of tl?.scenes || []) m[s.scene_id] = s;
    return m;
  }, [tl]);

  const ordered = useMemo(() => {
    const out = [];
    let cur = null;
    let n = 0;
    for (const el of elements || []) {
      if (el.type === "scene_heading") {
        n += 1;
        cur = { sceneId: el.id, number: n, els: [el] };
        out.push(cur);
      } else if (cur) cur.els.push(el);
    }
    const defaultLane = lanes[0]?.id;
    const arr = out.map((sc) => {
      const meta = metaById[sc.sceneId] || {};
      return {
        ...sc,
        laneId: laneById[meta.lane_id] ? meta.lane_id : defaultLane,
        col: typeof meta.story_order === "number" ? meta.story_order : sc.number - 1,
        label: meta.label || "",
      };
    });
    if (order === "story") arr.sort((a, b) => a.col - b.col || a.number - b.number);
    return arr;
  }, [elements, metaById, laneById, lanes, order]);

  const segments = useMemo(() => {
    const segs = [];
    for (const sc of ordered) {
      if (selected.has(sc.laneId)) segs.push({ type: "scene", sc });
      else {
        const last = segs[segs.length - 1];
        if (last && last.type === "stub") last.scenes.push(sc);
        else segs.push({ type: "stub", scenes: [sc] });
      }
    }
    return segs;
  }, [ordered, selected]);

  const visibleCount = segments.filter((s) => s.type === "scene").length;

  const toggleLane = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const togglePeek = (key) => setPeeks((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const stubLabel = (scenesArr) => {
    const counts = {};
    for (const sc of scenesArr) {
      const name = laneById[sc.laneId]?.name || "Unassigned";
      counts[name] = (counts[name] || 0) + 1;
    }
    if (order === "story") {
      const parts = Object.entries(counts).map(([nm, c]) => `${c} scene${c > 1 ? "s" : ""} in ${nm}`);
      return `meanwhile: ${parts.join(", ")}`;
    }
    const total = scenesArr.length;
    return `${total} scene${total > 1 ? "s" : ""} hidden (${Object.keys(counts).join(" · ")})`;
  };

  const renderScene = (sc, muted) => (
    <div
      key={sc.sceneId}
      onClick={() => onJumpToScene(sc.sceneId)}
      className={`relative rounded-sm cursor-pointer transition-colors hover:bg-[rgba(211,110,90,0.05)] group pl-3 pr-1 py-1 -ml-3 ${muted ? "opacity-55" : ""}`}
      style={{ borderLeft: `3px solid ${laneById[sc.laneId]?.color || "#bbb"}` }}
      title="Open in the editor at this scene"
      data-testid={`lane-read-scene-${sc.number}`}
    >
      <div className="flex items-center gap-2 text-[10px] font-sans uppercase tracking-widest text-[var(--ink-muted)] select-none">
        <span>Sc {sc.number}</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-2 h-2 rounded-full inline-block" style={{ background: laneById[sc.laneId]?.color || "#bbb" }} />
          {laneById[sc.laneId]?.name || "Unassigned"}
        </span>
        {sc.label && <span className="text-[var(--brand)]">{sc.label}</span>}
        <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[var(--brand)]">Open in editor →</span>
      </div>
      {sc.els.map((el) => (
        <div key={el.id} className={`script-line el-${el.type}`}>{el.text}</div>
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-[#efe9df]" data-testid="lane-read-view">
      {/* banner — unmistakable "you are reading a filtered script" */}
      <div className="shrink-0 bg-[#2d2a26] text-white shadow-md" data-testid="lane-read-banner">
        <div className="max-w-[8.5in] mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-2 font-display text-base shrink-0">
            <BookOpen size={15} className="text-[var(--brand)]" /> Filtered read
          </span>
          <span className="text-[11px] text-white/60 shrink-0" data-testid="lane-read-counts">
            {visibleCount} of {ordered.length} scenes · {selected.size} of {lanes.length} lanes
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {lanes.map((l) => {
              const on = selected.has(l.id);
              return (
                <button
                  key={l.id}
                  onClick={() => toggleLane(l.id)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "text-white border-transparent" : "text-white/55 border-white/25 hover:border-white/50"}`}
                  style={on ? { background: l.color } : {}}
                  data-testid={`lane-read-chip-${l.id}`}
                >
                  {l.name}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 ml-auto shrink-0">
            <div className="flex rounded-md border border-white/25 overflow-hidden text-[11px]">
              <button onClick={() => setOrder("script")} className={`px-2.5 py-1 ${order === "script" ? "bg-[var(--brand)] text-white" : "text-white/60 hover:text-white"}`} data-testid="lane-read-order-script">Script order</button>
              <button onClick={() => setOrder("story")} className={`px-2.5 py-1 ${order === "story" ? "bg-[var(--brand)] text-white" : "text-white/60 hover:text-white"}`} data-testid="lane-read-order-story">Story order</button>
            </div>
            <button onClick={onClose} className="flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 transition-colors" data-testid="lane-read-close">
              <X size={13} /> Show full script
            </button>
          </div>
        </div>
      </div>

      {/* body */}
      <div className="flex-1 overflow-y-auto py-6 px-3">
        <div className="screenplay-canvas-wrap">
          <div className={`screenplay-page ${isMobile ? "mobile-format" : ""}`} style={{ minHeight: "auto", cursor: "default", paddingTop: "0.6in", paddingBottom: "0.8in" }}>
            {!tl ? (
              <p className="font-sans text-sm text-[var(--ink-muted)]">Loading…</p>
            ) : selected.size === 0 ? (
              <p className="font-sans text-sm text-[var(--ink-muted)] italic" data-testid="lane-read-empty">No lanes selected — tap a lane chip in the bar above to start reading.</p>
            ) : (
              segments.map((seg, i) => {
                if (seg.type === "scene") return renderScene(seg.sc, false);
                const key = `stub-${i}-${seg.scenes[0]?.sceneId}`;
                const open = peeks.has(key);
                return (
                  <div key={key} className="my-4" data-testid={`lane-read-stub-${i}`}>
                    <button
                      onClick={() => togglePeek(key)}
                      className="w-full flex items-center gap-2 font-sans text-[11px] text-[var(--ink-muted)] hover:text-[var(--ink-secondary)] transition-colors select-none"
                      data-testid={`lane-read-stub-toggle-${i}`}
                    >
                      <span className="flex-1 border-t border-dashed border-[var(--line)]" />
                      {open ? <EyeOff size={12} /> : <Eye size={12} />}
                      <span className="italic">{stubLabel(seg.scenes)}</span>
                      <span className="flex-1 border-t border-dashed border-[var(--line)]" />
                    </button>
                    {open && <div className="mt-3">{seg.scenes.map((sc) => renderScene(sc, true))}</div>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
